import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, HelpCircle } from 'lucide-react';
import {
  Accordion, AccordionItem, AccordionTrigger, AccordionContent,
} from './ui/accordion';
import { Button } from './ui/button';

/**
 * FaqPreview — compact accordion used on the Homepage and the organiser CTA
 * section. `audience` controls the visual treatment so the same component
 * works on both the cream guest section and the dark heritage-green
 * organiser section.
 *
 * Props:
 *  - items: [{ q, a, comingSoon? }]
 *  - audience: 'guests' | 'organisers' (default 'guests')
 *  - eyebrow, title, subtitle, ctaLabel, ctaTo: section copy + CTA
 *  - testIdPrefix: data-testid namespace
 */
const FaqPreview = ({
  items,
  audience = 'guests',
  eyebrow = 'Frequently Asked',
  title,
  subtitle,
  ctaLabel,
  ctaTo,
  faqLinkTab = audience,
  testIdPrefix = 'faq-preview',
}) => {
  const dark = audience === 'organisers';

  // Theme tokens
  const surface = dark
    ? 'bg-white/[0.04] border-white/15'
    : 'bg-white border-[#E5E9E6]';
  const triggerText = dark ? 'text-white' : 'text-[#1F3D2B]';
  const contentText = dark ? 'text-white/75' : 'text-[#4A5D50]';
  const dividers = dark ? 'divide-white/10' : 'divide-[#E5E9E6]';
  const eyebrowPill = dark
    ? 'bg-white/[0.06] border-white/15 text-white/70'
    : 'bg-white border-[#E5E9E6] text-[#4A5D50]';
  const titleColor = dark ? 'text-white' : 'text-[#112318]';
  const subtitleColor = dark ? 'text-white/80' : 'text-[#4A5D50]';
  const primaryBtn = dark
    ? 'bg-white text-[#1F3D2B] hover:bg-[#F4F6F3]'
    : 'bg-[#1F3D2B] text-white hover:bg-[#172E20]';
  const linkColor = dark ? 'text-[#C9A27A] hover:text-white' : 'text-[#1F3D2B] hover:text-[#172E20]';

  return (
    <div className="max-w-3xl mx-auto" data-testid={testIdPrefix}>
      <div className="text-center mb-8 sm:mb-10">
        <div className={`inline-flex items-center gap-2 px-3 py-1 rounded-full border text-[10px] tracking-[0.18em] uppercase mb-4 ${eyebrowPill}`}>
          <HelpCircle className="w-3.5 h-3.5 text-[#C9A27A]" />
          {eyebrow}
        </div>
        {title && (
          <h2
            className={`text-2xl sm:text-3xl lg:text-4xl font-semibold tracking-tight leading-tight mb-3 ${titleColor}`}
            style={{ fontFamily: 'Playfair Display, serif' }}
          >
            {title}
          </h2>
        )}
        {subtitle && (
          <p className={`text-base leading-relaxed ${subtitleColor} max-w-xl mx-auto`}>
            {subtitle}
          </p>
        )}
      </div>

      <Accordion
        type="single"
        collapsible
        className={`w-full rounded-2xl border shadow-sm ${surface} ${dividers} divide-y`}
      >
        {items.map((item, i) => (
          <AccordionItem
            key={item.q}
            value={`${testIdPrefix}-${i}`}
            className="px-5 sm:px-6 border-b-0"
            data-testid={`${testIdPrefix}-item-${i}`}
          >
            <AccordionTrigger
              className={`text-left py-4 font-medium text-sm sm:text-[15px] hover:no-underline ${triggerText}`}
              data-testid={`${testIdPrefix}-trigger-${i}`}
            >
              <span className="flex-1 pr-3 leading-snug">{item.q}</span>
            </AccordionTrigger>
            <AccordionContent className={`text-sm leading-relaxed pb-5 ${contentText}`}>
              {item.a}
            </AccordionContent>
          </AccordionItem>
        ))}
      </Accordion>

      {/* Footer row: see all + CTA */}
      <div className="mt-8 flex flex-col sm:flex-row items-center justify-center gap-4">
        <Link
          to={`/faq?tab=${faqLinkTab}`}
          className={`inline-flex items-center text-sm font-medium ${linkColor} group`}
          data-testid={`${testIdPrefix}-see-all`}
        >
          See all FAQs
          <ArrowRight className="w-4 h-4 ml-1 transition-transform group-hover:translate-x-0.5" />
        </Link>
        {ctaLabel && ctaTo && (
          <Link to={ctaTo} data-testid={`${testIdPrefix}-cta`}>
            <Button className={`rounded-full px-6 h-11 ${primaryBtn}`}>
              {ctaLabel}
              <ArrowRight className="w-4 h-4 ml-1.5" />
            </Button>
          </Link>
        )}
      </div>
    </div>
  );
};

export default FaqPreview;
